import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, downloadFile, uploadFile } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { formatDateTime } from '../lib/format'
import { Card, EmptyState, ErrorBox, Loading } from './ui'

/** Et dokument på en enhed (beslutning 109). */
export interface DeviceFile {
  id: number
  name: string
  contentType: string
  sizeBytes: number
  /** drawing, datasheet, manual, certificate eller other. */
  category: string
  caption?: string | null
  uploadedBy?: { id: number; displayName: string } | null
  uploadedAt: string
}

export const deviceFileCategories: { value: string; label: string }[] = [
  { value: 'drawing', label: 'Tavletegning' },
  { value: 'datasheet', label: 'Datablad' },
  { value: 'manual', label: 'Manual' },
  { value: 'certificate', label: 'Certifikat' },
  { value: 'other', label: 'Andet' }
]

const labelOf = (category: string) => deviceFileCategories.find((c) => c.value === category)?.label ?? 'Andet'

function sizeText(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} kB`
}

/**
 * Enhedens dokumenter - tavletegninger, datablade, manualer og certifikater (beslutning 109). Grupperet efter
 * kategori, så tavletegningen er let at finde, når man står foran tavlen. Filerne ligger i enhedens mappe i
 * SharePoint og hentes gennem API-et.
 */
export function DeviceFilesCard({ deviceId }: { deviceId: number }) {
  const { isProjectManager } = useAuth()
  const client = useQueryClient()
  const key = ['devices', deviceId, 'files']
  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => api.get<DeviceFile[]>(`/api/devices/${deviceId}/files`)
  })

  const upload = useMutation({
    mutationFn: ({ file, category, caption }: { file: File; category: string; caption: string }) =>
      uploadFile<DeviceFile>(`/api/devices/${deviceId}/files`, file, file.name, { category, caption }),
    onSuccess: () => client.invalidateQueries({ queryKey: key })
  })
  const remove = useMutation({
    mutationFn: (fileId: number) => api.delete<void>(`/api/devices/${deviceId}/files/${fileId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: key })
  })

  const input = useRef<HTMLInputElement>(null)
  const [category, setCategory] = useState('drawing')
  const [caption, setCaption] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  async function pick(files: FileList | null) {
    if (!files || files.length === 0) return
    setProblem(null)
    try {
      for (const file of Array.from(files)) {
        await upload.mutateAsync({ file, category, caption })
      }
      setCaption('')
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Filen kunne ikke lægges op.')
    } finally {
      if (input.current) input.current.value = ''
    }
  }

  async function del(file: DeviceFile) {
    if (!confirm(`Slet "${file.name}"? Filen havner i SharePoints papirkurv.`)) return
    setProblem(null)
    try {
      await remove.mutateAsync(file.id)
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Filen kunne ikke slettes.')
    }
  }

  const groups = deviceFileCategories
    .map((c) => ({ ...c, files: (data ?? []).filter((f) => (labelOf(f.category) === c.label)) }))
    .filter((g) => g.files.length > 0)

  return (
    <Card title="Dokumenter">
      <p className="small muted" style={{ marginTop: 0 }}>
        Tavletegninger, datablade, manualer og certifikater til netop denne enhed. De ligger i enhedens mappe i
        SharePoint.
      </p>

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <select style={{ width: 'auto' }} value={category} onChange={(e) => setCategory(e.target.value)}>
          {deviceFileCategories.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <input
          style={{ flex: 1, minWidth: 160 }}
          placeholder="Beskrivelse (valgfri), f.eks. Hovedtavle rev. B"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
        <input
          ref={input}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => void pick(e.target.files)}
        />
        <button className="btn btn--primary btn--sm" disabled={upload.isPending} onClick={() => input.current?.click()}>
          {upload.isPending ? 'Lægger op ...' : 'Læg dokument op'}
        </button>
      </div>

      {problem && <ErrorBox error={problem} />}
      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}
      {data && data.length === 0 && <EmptyState>Der er ingen dokumenter på enheden endnu.</EmptyState>}

      <div className="stack" style={{ gap: 12 }}>
        {groups.map((group) => (
          <div key={group.value}>
            <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>
              {group.label}
            </div>
            <div className="stack" style={{ gap: 6 }}>
              {group.files.map((file) => (
                <div key={file.id} className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    className="link-button small"
                    style={{ textAlign: 'left', flex: 1, minWidth: 160 }}
                    onClick={() => void downloadFile(`/api/devices/${deviceId}/files/${file.id}/content`, file.name)}
                  >
                    📄 {file.caption || file.name}
                  </button>
                  <span className="tiny muted">
                    {sizeText(file.sizeBytes)} · {formatDateTime(file.uploadedAt)}
                    {file.uploadedBy ? ` · ${file.uploadedBy.displayName}` : ''}
                  </span>
                  {isProjectManager && (
                    <button className="btn btn--danger btn--sm" disabled={remove.isPending} onClick={() => void del(file)}>
                      Slet
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}
